import { DEFAULT_VALUE_BASIS, VALUE_BASIS_PARAM } from '@leo/model';
import { getValueBasis } from './basis-state';
import React from 'react';
import { Link } from 'react-router-dom';
import { meaning, t, type AskAnswer, type AskChip } from '@leo/model';
import { api } from '../api';
import { DataTable } from './DataTable';
import { BasisChips } from './kit';

type Ctx = { ask: (q: { question?: string; chip?: string; screen: string; filters?: Record<string, string> }) => void; answer: AskAnswer | null; question: string | null; busy: boolean; clear: () => void };
const AskCtx = React.createContext<Ctx | null>(null);

/** Holds the current question/answer so the top-bar box and the on-page panel share one state. */
export function AskProvider({ children }: { children: React.ReactNode }) {
  const [answer, setAnswer] = React.useState<AskAnswer | null>(null); const [question, setQuestion] = React.useState<string | null>(null); const [busy, setBusy] = React.useState(false);
  const ask: Ctx['ask'] = async (q) => {
    setBusy(true); setQuestion(q.question ?? null); setAnswer(null);
    // the value basis chosen goes with every question, so a sales answer is on it (and says so)
    const vb = getValueBasis(); const filters = vb === DEFAULT_VALUE_BASIS ? q.filters : { ...(q.filters ?? {}), [VALUE_BASIS_PARAM]: vb };
    try { setAnswer(await api<AskAnswer>('/api/v1/ask', { method: 'POST', body: JSON.stringify({ ...q, ...(filters ? { filters } : {}) }) })); }
    catch (e) { setAnswer({ ok: false, prose: `LEO could not answer just now (${(e as Error).message}). The screens still show the figures.`, basis: [], followUps: [] }); }
    finally { setBusy(false); }
  };
  return <AskCtx.Provider value={{ ask, answer, question, busy, clear: () => { setAnswer(null); setQuestion(null); } }}>{children}</AskCtx.Provider>;
}
export const useAsk = () => { const c = React.useContext(AskCtx); if (!c) throw new Error('AskProvider missing'); return c; };

const recentKey = (screen: string) => `leo.ask.recent.${screen}`;
const recent = (screen: string): string[] => { try { return JSON.parse(localStorage.getItem(recentKey(screen)) ?? '[]'); } catch { return []; } };
const remember = (screen: string, q: string) => { try { localStorage.setItem(recentKey(screen), JSON.stringify([q, ...recent(screen).filter(x => x !== q)].slice(0, 5))); } catch { /* per-viewer convenience only */ } };

/** The question box in the top bar. ⌘K focuses it. */
export function AskBar({ screen }: { screen: string }) {
  const { ask, busy } = useAsk(); const ref = React.useRef<HTMLInputElement>(null); const [v, setV] = React.useState('');
  React.useEffect(() => { const h = (e: KeyboardEvent) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); ref.current?.focus(); } }; window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h); }, []);
  const submit = (e: React.FormEvent) => { e.preventDefault(); const q = v.trim(); if (!q) return; remember(screen, q); ask({ question: q, screen }); setV(''); };
  return (
    <form className="askbar" onSubmit={submit} title={meaning('nav.ask')}>
      <span className="glyph">L</span>
      <input ref={ref} value={v} onChange={e => setV(e.target.value)} placeholder={busy ? 'Thinking…' : 'Ask LEO anything about the business…'} aria-label={t('nav.ask')} disabled={busy} />
      <kbd>⌘K</kbd>
    </form>
  );
}

/** The on-page panel: suggested questions for this screen, recent ones, and the answer. */
export function AskPanel({ screen, filters }: { screen: string; filters?: Record<string, string> }) {
  const { ask, answer, question, busy, clear } = useAsk();
  const [chips, setChips] = React.useState<AskChip[]>([]);
  React.useEffect(() => { let on = true; api<{ chips: AskChip[] }>(`/api/v1/ask/suggested?screen=${screen}`).then(r => on && setChips(r.chips)).catch(() => on && setChips([])); return () => { on = false; }; }, [screen]);
  const [chipText, setChipText] = React.useState<string | null>(null);
  const rec = recent(screen);
  return (
    <section className="band ask" id="ask">
      <header><h2 title={meaning('ask_about_this')}>{t('ask_about_this')}</h2>{answer && <button className="link" onClick={clear}>Clear</button>}</header>
      <div className="chips">
        {chips.map(c => <button key={c.id} onClick={() => { setChipText(c.text); ask({ chip: c.id, screen, filters }); }} disabled={busy}>{c.text}</button>)}
        {rec.map(q => <button key={'r' + q} className="recent" onClick={() => { setChipText(q); ask({ question: q, screen, filters }); }} disabled={busy} title="You asked this before">{q}</button>)}
      </div>
      {busy && <div className="answer busy"><div className="q">{question ?? chipText}</div><p className="p muted">Working it out from the bills and stock…</p></div>}
      {answer && !busy && <AnswerCard answer={answer} question={question ?? chipText} onFollowUp={q => { setChipText(q); ask({ question: q, screen, filters }); }} />}
    </section>
  );
}

export function AnswerCard({ answer, question, onFollowUp }: { answer: AskAnswer; question: string | null; onFollowUp?: (q: string) => void }) {
  return (
    <article className={`answer ${answer.unavailable ? 'unavailable' : ''}`}>
      {question && <div className="q">{question}</div>}
      {answer.prose && <p className="p">{answer.prose}</p>}
      {answer.unavailable && <p className="p"><b>{t('not_available_yet')}.</b> {answer.unavailable.missing} <span className="muted">What unlocks it: {answer.unavailable.unlocks}</span></p>}
      {answer.table && (
        <DataTable rows={answer.table.rows.map((r, i) => ({ key: String(i), ...Object.fromEntries(r.map((v, j) => [`c${j}`, v])) }))} exportName="ask-leo-answer" title={question ?? 'Ask LEO answer'} filter={answer.table.rows.length > 8} pageSize={50}
          columns={answer.table.columns.map((c, j) => ({ key: `c${j}`, title: c, kind: answer.table!.rows.every(r => r[j] == null || typeof r[j] === 'number') && answer.table!.rows.some(r => typeof r[j] === 'number') ? 'units' as const : 'text' as const }))} />
      )}
      <div className="foot">
        <BasisChips basis={answer.basis} />
        {answer.open && <Link to={answer.open.to + (answer.open.filters ? '?' + new URLSearchParams(answer.open.filters).toString() : '')} className="btn">{t('open')} →</Link>}
      </div>
      {answer.followUps.length > 0 && onFollowUp && <div className="chips">{answer.followUps.map(f => <button key={f} onClick={() => onFollowUp(f)}>{f}</button>)}</div>}
    </article>
  );
}
